export default class DecodingFilters {
  constructor(categoriesFakeNews) {
    this.categories = Object.values(categoriesFakeNews);
    this.selectedCategories = [];
  }

  /**
   * Return the list of categories.
   * @returns {*}
   */
  getFilterCategories() {
    return this.categories;
  }

  /**
   * Return category by id.
   * @param id
   * @returns {*}
   */
  getCategoryById(id) {
    return this.findCategoryInList(Object.values(this.categories), id);
  }

  /**
   * Return category by name.
   * @param name
   * @returns {*}
   */
  getCategoryByName(name) {
    return this.findCategoryInList(Object.values(this.categories), name, 'name');
  }

  /**
   * Return the first occuring category matching key => value.
   * @param Array list
   * @param mixed value
   * @param string key
   * @returns {*}
   * @private
   */
  findCategoryInList(list, value, key = 'id', recursive = true) {
    let cat = null;

    list.some((category) => {
      if (category[key] === value) {
        cat = category;
      } else if (recursive && category.childs) {
        cat = this.findCategoryInList(Object.values(category.childs), value, key);
      }
      return cat;
    });

    return cat;
  }

  /**
   * Parse categories tree and call callback method for each one.
   * @param list
   * @param callback
   */
  parseCategories(list, callback) {
    list.forEach((category) => {
      callback(category);
      if (category.childs) {
        this.parseCategories(Object.values(category.childs), callback);
      }
    });
  }

  /**
   * Return the list of parents.
   * @param category
   * @param list
   * @returns {*[]}
   * @private
   */
  getParentCategoriesList(category, list) {
    let parents;
    list.some((item) => {
      if (item.childs) {
        // Direct parent
        if (this.findCategoryInList(Object.values(item.childs), category.id, 'id', false)) {
          parents = [item];
          return item;
        }
        // Parents list.
        const parentsList = this.getParentCategoriesList(category, Object.values(item.childs));
        if (parentsList) {
          parents = [item].concat(parentsList);
        }
      }
      return false;
    });
    return parents;
  }

  /**
   * Return the parent categories.
   * @param category
   * @returns {*[]}
   */
  getParentCategories(category) {
    return this.getParentCategoriesList(category, Object.values(this.categories));
  }

  /**
   * return selection current options with default values.
   * @param opt
   * @returns {*&{selectChildren: boolean, enableParent: boolean, unSelectBrothers: boolean, force: boolean}}
   */
  static getSelectionOption(opt) {
    return {
      ...{
        selectChildren: true,
        unSelectBrothers: true,
        enableParent: true,
        force: false,
      },
      ...opt,
    };
  }

  /**
   * Select category.
   * @param category
   * @param selectChildren
   * @param unSelectBrothers
   * @param force
   */
  selectCategory(category, opts = {}) {
    const options = DecodingFilters.getSelectionOption(opts);
    // Add to selected categories list.
    if (options.force || !this.isCategorySelected(category)) {
      if (options.unSelectBrothers) {
        this.selectedCategories.forEach((cat) => {
          const el = cat;
          el.isEnabled = false;
        });
        this.selectedCategories = [category];
      } else {
        this.selectedCategories.push(category);
      }
      this.cleanSelectedCategory(category);
    }
    if (!options.isChild) {
      this.enableCategory(category, opts);
    }
  }

  /**
   * Unselect category.
   * @param category
   */
  unSelectCategory(category) {
    if (this.isCategorySelected(category)) {
      // eslint-disable-next-line no-param-reassign
      this.selectedCategories = this.selectedCategories.filter((cat) => cat !== category);
      this.cleanSelectedCategory(category);
    }
    DecodingFilters.disableCategory(category);
  }

  /**
   * Toggle category selection.
   * @param category
   */
  toggleCategory(category) {
    if (this.isCategorySelected(category)) {
      // When all children categories of a parent category
      // are disabled, they are in reality visually disabled
      // but selected in order to always have results.
      // (@see this.allChildrenCategoriesAreSelected)
      // So when we toggle a child category in this case,
      // the state is disabled and selected, so the expected  behaviors
      // is to deselect all other brothers categories.
      if (!DecodingFilters.isCategoryEnabled(category)) {
        this.selectCategory(category, {
          unSelectBrothers: true,
        });
      } else {
        this.unSelectCategory(category);
      }
    } else {
      this.selectCategory(category, {
        unSelectBrothers: false,
      });
    }
  }

  /**
   * Category selected state.
   * @param category
   * @returns {(function(*): *|boolean)|*|boolean}
   */
  isCategorySelected(category) {
    return this.selectedCategories.includes(category);
  }

  /**
   * Select all categories
   */
  selectAllCategories() {
    this.selectedCategories = [];
    this.parseCategories(Object.values(this.categories), (cat) => {
      DecodingFilters.disableCategory(cat);
    });
  }

  /**
   * Visually enable category.
   * @param category
   * @param options
   */
  enableCategory(category, opts) {
    const options = DecodingFilters.getSelectionOption(opts);

    if (options.force || !DecodingFilters.isCategoryEnabled(category)) {
      // eslint-disable-next-line no-param-reassign
      category.isEnabled = true;

      // Select parent category.
      const parent = this.getParentCategories(category)?.pop();
      if (options.enableParent && parent) {
        this.enableCategory(parent, { selectChildren: false });
      }

      // Select children categories.
      if (options.selectChildren && category.childs) {
        Object.values(category.childs).forEach((cat) => this.selectCategory(cat, {
          selectChildren: true,
          isChild: true,
          enableParent: false,
          unSelectBrothers: false,
        }));
      }

      if (options.unSelectBrothers) {
        Object.values(parent ? parent.childs : this.categories)
          .filter((cat) => cat !== category)
          .forEach((cat) => {
            this.unSelectCategory(cat);
          });
      }
    }
  }

  /**
   * Disable category.
   * @param category
   */
  static disableCategory(category) {
    if (DecodingFilters.isCategoryEnabled(category)) {
      // eslint-disable-next-line no-param-reassign
      category.isEnabled = false;
    }
  }

  /**
   * Category enabled (visual) state.
   * @param category
   * @returns {*}
   */
  static isCategoryEnabled(category) {
    return category?.isEnabled;
  }

  /**
   * Selected categories.
   * @returns {[]}
   */
  getSelectedCategories() {
    return this.selectedCategories;
  }

  /**
   * Return true if all children categories are selected.
   */
  allChildrenCategoriesAreSelected(parent) {
    const children = Object.values(parent.childs);
    const selectedChildrenCount = children.filter((cat) => !this.isCategorySelected(cat))
      .length;
    return (selectedChildrenCount === 0) || (selectedChildrenCount === children.length);
  }

  /**
   * Clean selected categories in order to avoid mismatch between
   * parent and child selection.
   * For example, when no child category is enabled, all child category should
   * be selected in order to always have consistent results.
   * @param category
   */
  cleanSelectedCategory(category) {
    const parent = this.getParentCategories(category)?.pop();

    if (parent && this.allChildrenCategoriesAreSelected(parent)) {
      this.selectedCategories = Object.values(parent.childs);
    }
  }
}
